/**
 * useReservationLifecycle — grouped five-operation mutation hook for the
 * booking lifecycle (SCRUM-335). Returns
 * `{ confirm, hold, checkIn, checkOut, cancel }`, each a TanStack
 * `useMutation` result.
 *
 * Models after `property/api/use-ownership-mutations.ts` (a multi-op hook
 * with a shared success-side invalidation helper) — NOT the single guests
 * If-Match hook.
 *
 * CRITICAL — booking OCC surfaces as a server-side 409, never a 412
 * (`domain/reservation/errors.ts`, SCRUM-277): the expected version is
 * captured INSIDE the tx, so the client sends NO `If-Match` header and NO
 * version var. A concurrent-write race is a plain 409 with a stable body
 * (no version echo). The lifecycle hooks therefore branch on 409
 * (`conflict`), NOT on 412.
 *
 * Each `mutationFn`:
 *   - POSTs `/organizations/{slug}/reservations/{id}/<op>` with
 *     `params.path` only (cancel additionally sends `body: { pathway }`)
 *   - unwraps the double envelope on success: the openapi-fetch
 *     `{ data, response, error }` union's `data` is the API envelope
 *     `{ data: ReservationLifecycleResultDto }` → resolves `result.data.data`
 *   - maps errors to a tagged union `{ kind }`: 409 → `conflict`,
 *     422 → `illegal` (illegal FSM transition / cancel pathway-gate),
 *     403 → `forbidden`, 404 → `notFound`, any other non-ok → `error`
 *   - on success `await`s `invalidateQueries` for both
 *     `bookingQueryKeys.detail(reservationId)` AND `bookingQueryKeys.lists()`
 *
 * NEVER optimistically flips status — the pill re-renders from the
 * refetched detail. Never fakes success.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { bookingQueryKeys } from './queryKeys.js';
import type { ReservationLifecycleResultDto } from './types.js';

/** Tagged error kinds the action cluster branches on. */
export type LifecycleErrorKind =
  | 'conflict' // 409 — OCC / dates / lock race (reload and retry)
  | 'illegal' // 422 — illegal FSM transition / cancel pathway-gate
  | 'forbidden' // 403 — lacks the op's permission slug
  | 'notFound' // 404 — absent / cross-org (anti-enum)
  | 'error'; // any other non-ok

/** A lifecycle op failure carrying the mapped `kind` for the UI. */
export interface LifecycleError extends Error {
  readonly kind: LifecycleErrorKind;
}

/** Cancel variables — the disbursement pathway (null = HOLD slot release). */
export interface CancelVariables {
  readonly pathway: string | null;
}

/** The grouped result — each op is a full TanStack mutation object. */
export interface UseReservationLifecycleResult {
  readonly confirm: UseMutationResult<
    ReservationLifecycleResultDto,
    LifecycleError,
    void
  >;
  readonly hold: UseMutationResult<
    ReservationLifecycleResultDto,
    LifecycleError,
    void
  >;
  readonly checkIn: UseMutationResult<
    ReservationLifecycleResultDto,
    LifecycleError,
    void
  >;
  readonly checkOut: UseMutationResult<
    ReservationLifecycleResultDto,
    LifecycleError,
    void
  >;
  readonly cancel: UseMutationResult<
    ReservationLifecycleResultDto,
    LifecycleError,
    CancelVariables
  >;
}

/** Maps an HTTP status to the tagged error kind (booking OCC = 409). */
function kindFor(status: number): LifecycleErrorKind {
  if (status === 409) return 'conflict';
  if (status === 422) return 'illegal';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  return 'error';
}

/** Builds a `LifecycleError` for a mapped HTTP failure. */
function lifecycleError(status: number): LifecycleError {
  const err = new Error(`lifecycle op failed (${status})`) as Error & {
    kind: LifecycleErrorKind;
  };
  err.kind = kindFor(status);
  return err;
}

/**
 * The narrow structural shape of an openapi-fetch lifecycle POST result
 * that `unwrapLifecycle` reads. Every lifecycle endpoint returns the
 * `{ data: ReservationLifecycleResultDto }` API envelope in the openapi-fetch
 * `{ data, response, error }` union; this is the minimal surface the unwrap
 * touches (typing against `typeof apiClient.POST<…>` is not possible — the
 * generic `ClientMethod` overload set has no `typeof`-applicable signature).
 */
interface LifecyclePostResult {
  readonly response: Response;
  readonly data?: { readonly data: ReservationLifecycleResultDto } | undefined;
}

/**
 * Unwraps a lifecycle POST result: throws a mapped `LifecycleError` on any
 * non-ok status, otherwise resolves the inner `result.data.data` payload
 * (the `{ data: ReservationLifecycleResultDto }` API envelope).
 */
function unwrapLifecycle(
  result: LifecyclePostResult,
): ReservationLifecycleResultDto {
  if (!result.response.ok || result.data === undefined) {
    throw lifecycleError(result.response.status);
  }
  return result.data.data;
}

export function useReservationLifecycle(
  reservationId: string,
): UseReservationLifecycleResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  /** Shared success-side invalidation: fresh detail + every list. */
  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: bookingQueryKeys.detail(reservationId),
    });
    await queryClient.invalidateQueries({
      queryKey: bookingQueryKeys.lists(),
    });
  };

  const confirm = useMutation<ReservationLifecycleResultDto, LifecycleError>({
    mutationFn: async () => {
      if (orgSlug === null) throw lifecycleError(0);
      const result = await apiClient.POST(
        '/organizations/{slug}/reservations/{id}/confirm',
        { params: { path: { slug: orgSlug, id: reservationId } } },
      );
      const payload = unwrapLifecycle(result);
      await invalidate();
      return payload;
    },
  });

  const hold = useMutation<ReservationLifecycleResultDto, LifecycleError>({
    mutationFn: async () => {
      if (orgSlug === null) throw lifecycleError(0);
      const result = await apiClient.POST(
        '/organizations/{slug}/reservations/{id}/hold',
        { params: { path: { slug: orgSlug, id: reservationId } } },
      );
      const payload = unwrapLifecycle(result);
      await invalidate();
      return payload;
    },
  });

  const checkIn = useMutation<ReservationLifecycleResultDto, LifecycleError>({
    mutationFn: async () => {
      if (orgSlug === null) throw lifecycleError(0);
      const result = await apiClient.POST(
        '/organizations/{slug}/reservations/{id}/check-in',
        { params: { path: { slug: orgSlug, id: reservationId } } },
      );
      const payload = unwrapLifecycle(result);
      await invalidate();
      return payload;
    },
  });

  const checkOut = useMutation<ReservationLifecycleResultDto, LifecycleError>({
    mutationFn: async () => {
      if (orgSlug === null) throw lifecycleError(0);
      const result = await apiClient.POST(
        '/organizations/{slug}/reservations/{id}/check-out',
        { params: { path: { slug: orgSlug, id: reservationId } } },
      );
      const payload = unwrapLifecycle(result);
      await invalidate();
      return payload;
    },
  });

  const cancel = useMutation<
    ReservationLifecycleResultDto,
    LifecycleError,
    CancelVariables
  >({
    mutationFn: async (vars) => {
      if (orgSlug === null) throw lifecycleError(0);
      const result = await apiClient.POST(
        '/organizations/{slug}/reservations/{id}/cancel',
        {
          params: { path: { slug: orgSlug, id: reservationId } },
          body: { pathway: vars.pathway },
        },
      );
      const payload = unwrapLifecycle(result as LifecyclePostResult);
      await invalidate();
      return payload;
    },
  });

  return { confirm, hold, checkIn, checkOut, cancel };
}
